import type { FunctionComponent } from 'react'
import React, { Fragment, useState } from 'react'
import type { Entity, EntityReaderPayload } from 'api/data.types'
import { useMoveUser } from '../MoveUser/MoveUser.hooks'
import {
  Modal,
  EntityArea,
  ParentEntity,
  BackArrowButton,
  RightArrowButton,
  ChildEntity,
  RightButton,
  BreakLine
} from '../MoveUser/MoveUser.styles'
import { useTranslation } from 'i18n'
import { useToast } from 'utils/hooks/use-toast/useToast'
import type { ApolloClient } from '@apollo/client'

type BulkMoveUserModalProps = {
  currentEntity: Entity
  selectedUserIds: number[]
  usersFromCurrentEntity: EntityReaderPayload[]
  isOpen: boolean
  client: ApolloClient<object>
  onClose: () => void
  onConfirm: (
    selectedUserIds: number[],
    usersFromCurrentEntity: EntityReaderPayload[],
    destinationEntityId: number
  ) => Promise<void>
}

const BulkMoveUserModal: FunctionComponent<BulkMoveUserModalProps> = ({
  currentEntity,
  selectedUserIds,
  usersFromCurrentEntity,
  isOpen,
  client,
  onClose,
  onConfirm
}) => {
  const { t } = useTranslation()
  const { toastMessage } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {
    containerRef,
    destinationEntity,
    selectedItems,
    selectedParent,
    renderedChildren,
    handleEntityClick,
    resetParentEntities,
    handleRenderedChildren
  } = useMoveUser({ client, currentEntity })

  const handleSubmit = async () => {
    if (destinationEntity && !isSubmitting) {
      setIsSubmitting(true)
      try {
        await onConfirm(
          selectedUserIds,
          usersFromCurrentEntity,
          destinationEntity.id
        )
        onClose()
      } catch (error) {
        console.error('Error moving users:', error)
        toastMessage({
          message: 'Erro ao mover usuários. Tente novamente.',
          type: 'error'
        })
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  return (
    <Modal
      show={isOpen}
      canCloseByXBtn
      title={`Mover usuários para`}
      onClose={onClose}
      role="dialog"
      aria-labelledby="bulk-move-user-modal-title"
      aria-describedby="bulk-move-user-modal-description"
    >
      <EntityArea ref={containerRef}>
        {Boolean(selectedItems?.length) &&
          selectedItems.map(item => {
            if (selectedParent && selectedParent.id !== item.id) {
              return
            }
            return (
              <Fragment key={item.id}>
                <ParentEntity
                  isSelected={destinationEntity === item}
                  hasOpenedChildren={Boolean(
                    renderedChildren?.length || item.ancestryDetails
                  )}
                  onClick={() => handleEntityClick(item)}
                >
                  {Boolean(item.ancestryDetails) && (
                    <BackArrowButton
                      iconName="arrow-left"
                      onClick={() => void resetParentEntities()}
                      theme={{ type: 'secondary', size: 'medium' }}
                      aria-label="Voltar para entidade pai"
                    />
                  )}
                  {item.name}
                  {!renderedChildren?.length && Boolean(item.childrenCount) && (
                    <RightArrowButton
                      iconName="caret-right"
                      onClick={() => handleRenderedChildren(item)}
                      theme={{ type: 'ghost', size: 'small' }}
                      aria-label={`Expandir filhos de ${item.name}`}
                    />
                  )}
                </ParentEntity>
                <BreakLine
                  isBold={Boolean(
                    renderedChildren?.length || item.ancestryDetails
                  )}
                />
              </Fragment>
            )
          })}
        {Boolean(renderedChildren?.length) &&
          renderedChildren.map(child => {
            return (
              <Fragment key={child.id}>
                <ChildEntity
                  isSelected={destinationEntity === child}
                  onClick={() =>
                    !child.children?.length && handleEntityClick(child)
                  }
                >
                  {child.name}
                  {Boolean(child.childrenCount) && (
                    <RightArrowButton
                      iconName="caret-right"
                      onClick={() => handleRenderedChildren(child)}
                      theme={{ type: 'ghost', size: 'small' }}
                      aria-label={`Expandir filhos de ${child.name}`}
                    />
                  )}
                </ChildEntity>
                <BreakLine isBold={false} />
              </Fragment>
            )
          })}
      </EntityArea>
      <RightButton
        data-testid={'submit-button'}
        disabled={!destinationEntity || isSubmitting}
        loading={isSubmitting}
        theme={{ type: 'primaryLivros', size: 'large' }}
        text={t('Mover para cá')}
        onClick={handleSubmit}
      />
    </Modal>
  )
}

export default BulkMoveUserModal
